import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { UseQueryOptions } from '@tanstack/react-query'
import type {
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import { Table } from './index'
import type { ColumnDefinition, SelectionAction, TableTab } from './types'
import { useTablePreferences } from './hooks/useTablePreferences'
import { useListQuery } from './hooks/useListQuery'
import { useDebouncedValue } from './hooks/useDebouncedValue'
import { apiSortToSorting, tableStateToListParams } from './listParams'
import type { ListParams, ListResult } from '../../api/listApi.types'

export interface QueryTableProps<TRow extends { id: string }> {
  tableId: string
  /** Authenticated user id for per-user table preferences in localStorage. */
  userId?: string
  columns: ColumnDefinition<TRow>[]
  fetchList: (params: ListParams) => Promise<ListResult<TRow>>
  searchPlaceholder?: string
  defaultSort?: { sort_by: string; order: 'asc' | 'desc' }
  defaultPageSize?: number
  enableSelection?: boolean
  selectionActions?: SelectionAction[]
  tabs?: TableTab[]
  fillContainer?: boolean
  emptyTitle?: string
  emptySubtitle?: string
  emptyIcon?: 'battery' | 'users'
  enableRowActions?: boolean
  enableColumnReorder?: boolean
  enableViewConfig?: boolean
  onRowClick?: (row: TRow) => void
  getRowAccent?: (row: TRow) => string | undefined
  /** @deprecated Use `getRowAccent`. */
  getRowStatus?: (row: TRow) => string
  disableLocalPersistence?: boolean
  searchDebounceMs?: number
  /** When this key changes, column filters reset to `initialColumnFilters`. */
  contextKey?: string
  initialColumnFilters?: ColumnFiltersState
  footer?: React.ReactNode
  queryOptions?: Omit<
    UseQueryOptions<ListResult<TRow>>,
    'queryKey' | 'queryFn'
  >
}

export function QueryTable<TRow extends { id: string }>({
  tableId,
  userId = '',
  columns,
  fetchList,
  searchPlaceholder = 'Search…',
  defaultSort = { sort_by: 'name', order: 'asc' },
  defaultPageSize = 25,
  enableSelection = false,
  selectionActions,
  tabs,
  fillContainer = false,
  emptyTitle,
  emptySubtitle,
  emptyIcon,
  enableRowActions = true,
  enableColumnReorder = true,
  enableViewConfig = true,
  onRowClick,
  getRowAccent,
  getRowStatus,
  disableLocalPersistence = false,
  searchDebounceMs = 300,
  contextKey,
  initialColumnFilters,
  footer,
  queryOptions,
}: QueryTableProps<TRow>) {
  const {
    prefs,
    orderedColumns,
    filterableColumns,
    allFilterColumns,
    onPaginationChange,
    onColumnFiltersChange,
    onSortChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onVisibleFilterIdsChange,
    onColumnSizingChange,
    setSearch,
    resetPageIndex,
  } = useTablePreferences({
    userId,
    tableId,
    columns,
    disablePersistence: disableLocalPersistence,
    defaults: {
      pagination: { pageIndex: 0, pageSize: defaultPageSize },
      columnFilters: initialColumnFilters ?? [],
      sort: defaultSort,
    },
  })

  useEffect(() => {
    if (contextKey == null) return
    onColumnFiltersChange(initialColumnFilters ?? [])
  }, [contextKey, initialColumnFilters, onColumnFiltersChange])

  const [search, setSearchLocal] = useState(prefs.search ?? '')
  const debouncedSearch = useDebouncedValue(search, searchDebounceMs)

  const sorting = useMemo(
    () => apiSortToSorting(prefs.sort, columns),
    [prefs.sort, columns],
  )

  const params = useMemo(
    () =>
      tableStateToListParams({
        pagination: prefs.pagination,
        sorting,
        columnFilters: prefs.columnFilters,
        search: debouncedSearch,
        columns,
      }),
    [prefs.pagination, sorting, prefs.columnFilters, debouncedSearch, columns],
  )

  const queryFn = useCallback(
    (p: ListParams) => fetchList(p),
    [fetchList],
  )

  const { data, total, isLoading, isFetching, error } = useListQuery<TRow>({
    queryKey: contextKey != null ? [tableId, 'list', contextKey] : [tableId, 'list'],
    params,
    queryFn,
    ...queryOptions,
  })

  const pageCount = Math.max(1, Math.ceil(total / prefs.pagination.pageSize))
  const loading = isLoading
  const fetching = isFetching && !isLoading

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const next =
        typeof updater === 'function' ? updater(sorting) : updater
      const first = next[0]
      const current = sorting[0]
      const target = first ?? current
      if (!target) return
      const col = columns.find((c) => c.id === target.id)
      onSortChange({
        sort_by: col?.sortKey ?? target.id,
        order: first ? (first.desc ? 'desc' : 'asc') : 'asc',
      })
    },
    [sorting, onSortChange, columns],
  )

  const onGlobalFilterChange = useCallback(
    (value: string) => {
      setSearchLocal(value)
      setSearch(value)
      resetPageIndex()
    },
    [setSearch, resetPageIndex],
  )

  const onColumnFiltersChangeWrapped: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      onColumnFiltersChange(updater)
    },
    [onColumnFiltersChange],
  )

  const columnVisibility = prefs.columnVisibility as VisibilityState

  if (error) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 8,
          border: '1px solid var(--color-border, #E5E7EB)',
          background: 'var(--color-muted, #F9FAFB)',
          color: '#991B1B',
          fontSize: 14,
        }}
        role="alert"
      >
        {error.message}
      </div>
    );
  }

  return (
    <Table
      columns={orderedColumns}
      rows={data}
      loading={loading}
      fetching={fetching}
      manualPagination
      manualFiltering
      pageCount={pageCount}
      rowCount={total}
      pagination={prefs.pagination}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
      columnFilters={prefs.columnFilters}
      onColumnFiltersChange={onColumnFiltersChangeWrapped}
      globalFilter={search}
      onGlobalFilterChange={onGlobalFilterChange}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
      columnOrder={prefs.columnOrder}
      onColumnOrderChange={onColumnOrderChange}
      visibleFilterIds={prefs.visibleFilterIds}
      onVisibleFilterIdsChange={onVisibleFilterIdsChange}
      filterableColumns={filterableColumns}
      allFilterColumns={allFilterColumns}
      enableColumnReorder={enableColumnReorder}
      enableViewConfig={enableViewConfig}
      columnSizing={prefs.columnSizing}
      onColumnSizingChange={onColumnSizingChange}
      searchPlaceholder={searchPlaceholder}
      selectionActions={selectionActions}
      enableSelection={enableSelection}
      enableRowActions={enableRowActions}
      tabs={tabs}
      emptyTitle={emptyTitle}
      emptySubtitle={emptySubtitle}
      emptyIcon={emptyIcon}
      fillContainer={fillContainer}
      onRowClick={onRowClick}
      getRowAccent={getRowAccent}
      getRowStatus={getRowStatus}
      footer={footer}
      selectionResetKey={contextKey}
    />
  )
}
